// src/components/common/EditSlider.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, GlobalStyles, IconButton, Stack, Typography, useTheme } from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FullscreenRoundedIcon from '@mui/icons-material/FullscreenRounded';
import FullscreenExitRoundedIcon from '@mui/icons-material/FullscreenExitRounded';

const openSliderStack = new Set();

export const EditSlider = ({
                               open,
                               title,
                               onClose,
                               children,
                               width = '80vw',
                               enableFullscreen = true,
                               initialFullscreen = false,
                               headerRight,
                               closeOnOutsideClick = false,
                               zIndexOffset = 0,
                               animateOnOpen = false,
                               slideDuration = null,
                           }) => {
    const theme = useTheme();
    const [isFullscreen, setIsFullscreen] = useState(!!initialFullscreen);
    const [hasEntered, setHasEntered] = useState(!animateOnOpen || !open);
    const sliderZ = (theme.zIndex?.modal ?? 1300) + 200 + zIndexOffset;
    const fullscreenSliderZ = (theme.zIndex?.tooltip ?? 1500) + 5000 + zIndexOffset;
    const transitionDuration = Number.isFinite(slideDuration)
        ? slideDuration
        : theme.transitions.duration.shorter;
    const sliderIdRef = useRef({});

    useEffect(() => {
        if (!open) return undefined;
        openSliderStack.add(sliderIdRef.current);
        return () => openSliderStack.delete(sliderIdRef.current);
    }, [open]);

    useEffect(() => {
        // Reset fullscreen state when slider is closed (optional)
        if (!open) setIsFullscreen(!!initialFullscreen);
    }, [open, initialFullscreen]);

    useEffect(() => {
        if (!animateOnOpen) {
            setHasEntered(true);
            return undefined;
        }

        if (!open) {
            setHasEntered(false);
            return undefined;
        }

        setHasEntered(false);
        const frame = requestAnimationFrame(() => setHasEntered(true));
        return () => cancelAnimationFrame(frame);
    }, [animateOnOpen, open]);

    useEffect(() => {
        if (!open) return undefined;
        const onKeyDown = (event) => {
            if (event.key === 'Escape' && [...openSliderStack].pop() === sliderIdRef.current) {
                event.preventDefault();
                onClose?.();
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [open, onClose]);

    const toggleFullscreen = useCallback(() => {
        setIsFullscreen((prev) => !prev);
    }, []);

    const containerSx = useMemo(() => {
        const base = {
            position: 'fixed',
            top: 75,
            right: 0,
            height: '90dvh',
            bgcolor: theme.palette.background.paper,
            boxShadow: theme.shadows[10],
            zIndex: sliderZ,
            display: 'flex',
            flexDirection: 'column',
            transform: open && hasEntered ? 'translateX(0)' : 'translateX(100%)',
            transition: theme.transitions.create('transform', {
                duration: transitionDuration,
                easing: theme.transitions.easing.easeOut,
            }),
        };

        if (isFullscreen) {
            return {
                ...base,
                left: 0,
                right: 0,
                width: '100vw',
                borderRadius: 0,
                zIndex: fullscreenSliderZ,
            };
        }

        return {
            ...base,
            width,
            maxWidth: '100vw',
            borderTopLeftRadius: 16,
            borderBottomLeftRadius: 16,
        };
    }, [theme, open, hasEntered, transitionDuration, width, isFullscreen, sliderZ, fullscreenSliderZ]);

    const contentSx = useMemo(() => ({
        flex: 1,
        overflow: 'auto',
        WebkitOverflowScrolling: 'touch',
    }), []);

    const slider = open ? (
        <>
            {isFullscreen && (
                <GlobalStyles
                    styles={{
                        '.MuiPopper-root': { zIndex: `${fullscreenSliderZ + 20} !important` },
                        '.MuiPopover-root': { zIndex: `${fullscreenSliderZ + 20} !important` },
                        '.MuiMenu-root': { zIndex: `${fullscreenSliderZ + 20} !important` },
                        '.MuiAutocomplete-popper': { zIndex: `${fullscreenSliderZ + 20} !important` },
                        '.MuiDataGrid-panel, .MuiDataGrid-menu': {
                            '--DataGrid-t-z-index-panel': `${fullscreenSliderZ + 20}`,
                            '--DataGrid-t-z-index-menu': `${fullscreenSliderZ + 20}`,
                            zIndex: `${fullscreenSliderZ + 20} !important`,
                        },
                    }}
                />
            )}
            {closeOnOutsideClick && (
                <Box
                    onClick={onClose}
                    sx={{
                        position: 'fixed',
                        inset: 0,
                        zIndex: sliderZ - 1,
                        backgroundColor: 'transparent',
                    }}
                />
            )}
            <Box sx={containerSx} role="dialog" aria-modal="true" data-edit-slider>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                        px: 2,
                        py: 1.5,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        minHeight: 56,
                    }}
                >
                    <Typography
                        variant="subtitle1"
                        sx={{
                            fontWeight: 700,
                            pr: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {title}
                    </Typography>

                    <Stack direction="row" alignItems="center" spacing={1}>
                        {headerRight || null}

                        {enableFullscreen && (
                            <IconButton onClick={toggleFullscreen} size="small" aria-label="toggle fullscreen">
                                {isFullscreen ? <FullscreenExitRoundedIcon /> : <FullscreenRoundedIcon />}
                            </IconButton>
                        )}

                        <IconButton onClick={onClose} size="small" aria-label="close slider">
                            <CloseRoundedIcon />
                        </IconButton>
                    </Stack>
                </Stack>

                <Box sx={contentSx}>{children}</Box>
            </Box>
        </>
    ) : null;

    // Render into body to avoid being trapped by transformed parents / stacking contexts
    return ReactDOM.createPortal(slider, document.body);
};
